'use client';

import { useRef, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import type { FAQContent, FAQItem } from '@/lib/content-schema';

interface Props {
  content: FAQContent;
  email: string;
}

const profileIds = ['club', 'organizadores'] as const;

function FAQAccordion({
  question,
  answer,
  isOpen,
  onToggle,
}: FAQItem & { isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="border-b border-zibarit-marron-secundario/30 last:border-b-0">
      <button onClick={onToggle} className="flex w-full items-center justify-between py-5 text-left cursor-pointer" aria-expanded={isOpen}>
        <span className="text-body text-base md:text-lg font-medium text-zibarit-gris-oscuro pr-4">{question}</span>
        <span
          className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full border border-zibarit-verde-oscuro/20 transition-transform duration-300"
          style={{ transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-zibarit-verde-oscuro" />
          </svg>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="text-body text-sm md:text-base text-zibarit-gris/90 pb-5 leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function FAQPageContent({ content, email }: Props) {
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, margin: '-50px' });
  const tabsRef = useRef(null);
  const tabsInView = useInView(tabsRef, { once: true, margin: '-80px' });

  const [activeProfile, setActiveProfile] = useState<string>('club');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => { setOpenIndex(null); }, [activeProfile]);

  const profiles = [
    { id: 'club', label: 'Club', items: content.club, disabled: false },
    { id: 'organizadores', label: 'Organizadores', items: content.pro, disabled: false },
  ];

  const activeItems = profiles.find((p) => p.id === activeProfile)?.items ?? [];

  return (
    <>
      <section className="pt-32 md:pt-40 pb-12 md:pb-16 bg-zibarit-bone">
        <motion.div ref={heroRef} variants={staggerContainer} initial="initial" animate={heroInView ? 'animate' : 'initial'} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.p variants={fadeInUp} className="text-subtitle text-xs tracking-[0.2em] text-zibarit-verde-oscuro mb-4">{content.hero.subtitle}</motion.p>
          <motion.h1 variants={fadeInUp} className="text-title text-4xl md:text-5xl lg:text-6xl text-zibarit-gris-oscuro">{content.hero.title}</motion.h1>
          <motion.p variants={fadeInUp} className="text-body text-base md:text-lg text-zibarit-gris mt-6 max-w-2xl mx-auto">{content.hero.description}</motion.p>
        </motion.div>
      </section>

      <section className="py-12 md:py-20 bg-zibarit-bone">
        <motion.div ref={tabsRef} variants={staggerContainer} initial="initial" animate={tabsInView ? 'animate' : 'initial'} className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-3 mb-12">
            {profiles.map((profile) => (
              <button
                key={profile.id}
                onClick={() => !profile.disabled && setActiveProfile(profile.id)}
                disabled={profile.disabled}
                className={`relative rounded-full px-5 py-2.5 text-sm font-bold uppercase tracking-wider transition-all duration-300 cursor-pointer ${
                  profile.disabled
                    ? 'bg-white/50 text-zibarit-gris border border-zibarit-gris-claro opacity-50 cursor-not-allowed pointer-events-none'
                    : activeProfile === profile.id
                      ? 'bg-zibarit-verde-oscuro text-zibarit-bone'
                      : 'bg-white text-zibarit-gris-oscuro border border-zibarit-marron-secundario/30 hover:border-zibarit-verde-oscuro/50'
                }`}
              >
                <span className="font-lato">{profile.label}</span>
              </button>
            ))}
          </motion.div>

          <motion.div variants={fadeInUp}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeProfile}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                {activeItems.map((item, i) => (
                  <FAQAccordion
                    key={i}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openIndex === i}
                    onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                  />
                ))}
                {activeProfile === 'organizadores' && (
                  <div className="mt-8 text-center">
                    <Link href="/precios" className="text-body text-sm text-zibarit-verde-oscuro hover:text-zibarit-verde-secundario transition-colors inline-flex items-center gap-1.5">
                      Ver planes y precios
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="mt-px"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </Link>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </section>

      <section className="bg-zibarit-verde-oscuro py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-title text-2xl md:text-3xl text-zibarit-bone mb-4">{content.bottomCta.title}</h2>
          <p className="text-body text-base text-zibarit-bone/70 mb-6">{content.bottomCta.description}</p>
          <a
            href={`mailto:${email}`}
            className="inline-block rounded-full border border-zibarit-bone/30 px-8 py-3 text-sm font-bold uppercase tracking-wider text-zibarit-bone hover:bg-zibarit-bone/10 transition-colors font-lato"
          >
            {content.bottomCta.buttonText}
          </a>
        </div>
      </section>
    </>
  );
}
